/**
 * Currency formatting helpers shared across the app.
 *
 * Stripe (and our payment backend) treat certain currencies as "zero-decimal":
 * the amount is stored as a whole number with no minor units (e.g. JPY 5000 is
 * ¥5,000, NOT ¥50.00). This mirrors the ZERO_DECIMAL set used server-side in
 * booking.ts / PaymentService.ts so client and server format identically.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/**
 * Format a monetary amount for display using the invitee/host's locale.
 *
 * @param amount   Numeric amount in the currency's major unit as stored on the
 *                 booking (already whole-number for zero-decimal currencies).
 * @param currency ISO 4217 code (e.g. 'JPY', 'USD'). Falls back to 'USD'.
 * @returns A localized currency string (e.g. "¥5,000", "$50.00"). On an
 *          unsupported currency code it degrades gracefully to "5000 XYZ".
 */
export function formatCurrency(amount: number, currency?: string | null): string {
  const cur = (currency || 'USD').toUpperCase();
  const minDigits = ZERO_DECIMAL_CURRENCIES.has(cur) ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: minDigits,
    }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}
